import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, ElementType } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { fetchTopicCards, fetchHowToCards, fetchRecentlyViewed } from 'services/confluenceService';
import { TopicCard as TopicCardType, HowToCard as HowToCardType, RecentlyViewedDocument } from 'services/types';
import { formatRelativeTime, truncateWords } from 'utility/helper';
import { useAppSelector } from 'store';
import { useKnowledgeHubSearch } from 'shared-compoents/useKnowledgeHubSearch';
import KnowledgeHubSearchResults from 'shared-compoents/search-results';
import {
  PageWrapper,
  HeroSection,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroImage,
  SearchSection,
  SearchInputWrapper,
  SearchInputIcon,
  SearchInput,
  SearchBtn,
  AskAssistantBtn,
  TryAskingSection,
  TryAskingLabel,
  TryAskingChip,
  SectionHeader,
  SectionTitle,
  ViewAllLink,
  TopicsColumn,
  TopicCardsGrid,
  TopicCard,
  TopicCardHeader,
  TopicCardIconWrapper,
  TopicCardTitle,
  TopicCardDescription,
  TopicCardDocsCount,
  RecentlyViewedSection,
  RecentlyViewedItem,
  RecentlyViewedIconBadge,
  RecentlyViewedIcon,
  RecentlyViewedItemTitle,
  RecentlyViewedItemTime,
  RecentlyViewedEmpty,
  RecentlyViewedEmptyIcon,
  RecentlyViewedEmptyTitle,
  RecentlyViewedEmptyText,
  RecentlyViewedBox,
  HowToCard,
  HowToCardIcon,
  HowToCardContent,
  HowToCardTitle,
  HowToCardDescription,
  ContentRow,
  SectionHeaderHowTo,
  ActionArrow,
  TopicCardsGridHowTo,
} from './styles';

const tryAskingSuggestions = [
  'What is TDSP Enclave?',
  'Databricks Cluster',
  'Fix Pipeline Failure',
  'Login to TMNA Github',
];

// Shows a tooltip with the full title only when it is visually truncated (clamped).
type ClampedTitleProps = Readonly<{ title: string; as: ElementType; maxLines?: number }>;

function ClampedTitle({ title, as: Component, maxLines = 2 }: ClampedTitleProps) {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) { return undefined; }
    const check = () => {
      const style = window.getComputedStyle(element);
      const lineHeight = Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.2;
      setIsTruncated(element.scrollHeight > lineHeight * maxLines + 1);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(element);
    // Re-measure once web fonts finish loading, since fallback fonts can distort wrapping.
    document.fonts?.ready?.then(check).catch(() => {});
    return () => observer.disconnect();
  }, [title, maxLines]);

  return (
    <Tooltip title={isTruncated ? title : ''} arrow>
      <Component ref={ref}>{title}</Component>
    </Tooltip>
  );
}

function ExploreTopics() {
  const navigate = useNavigate();
  const accessToken = useAppSelector((state) => state.userData.access_token);
  const [topics, setTopics] = useState<TopicCardType[]>([]);
  const [howToItems, setHowToItems] = useState<HowToCardType[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedDocument[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [loadingHowTo, setLoadingHowTo] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [showAllTopics, setShowAllTopics] = useState(false);

  const visibleTopics = showAllTopics ? topics : topics.slice(0, 6);

  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    hasSearched,
    clear: clearSearch,
  } = useKnowledgeHubSearch();
  const [searchFocused, setSearchFocused] = useState(false);
  const showSearchResults = searchFocused && searchQuery.trim().length >= 2;

  const handleSelectResult = (pageId: string) => {
    clearSearch();
    setSearchFocused(false);
    navigate(`/documentation/${pageId}`);
  };

  const handleSearchSubmit = () => {
    if (searchResults.length > 0) {
      handleSelectResult(searchResults[0].page_id);
    }
  };

  useEffect(() => {
    if (!accessToken) return;

    fetchTopicCards()
      .then((res) => {
        const sorted = [...res.topicCards].sort((a, b) => a.order - b.order);
        setTopics(sorted);
      })
      .catch(() => {})
      .finally(() => setLoadingTopics(false));

    fetchHowToCards()
      .then((res) => {
        const sorted = [...res.howToCards].sort((a, b) => a.order - b.order);
        setHowToItems(sorted);
      })
      .catch(() => {})
      .finally(() => setLoadingHowTo(false));

    fetchRecentlyViewed()
      .then((res) => setRecentlyViewed(res.documents))
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
  }, [accessToken]);

  return (
    <PageWrapper>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Knowledge Hub</HeroTitle>
          <HeroSubtitle>
            Find answers and learn best practices, and get the most out of the TDSP Enclave.
          </HeroSubtitle>
          <SearchSection>
            <SearchInputWrapper>
              <SearchInputIcon src="/assets/search-grey.svg" alt="" />
              <SearchInput
                type="text"
                placeholder="Search for documentation, self service request and more..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSearchSubmit();
                }}
              />
              {showSearchResults && (
                <KnowledgeHubSearchResults
                  results={searchResults}
                  loading={searchLoading}
                  error={searchError}
                  hasSearched={hasSearched}
                  query={searchQuery}
                  onSelect={handleSelectResult}
                />
              )}
            </SearchInputWrapper>
            <SearchBtn onClick={handleSearchSubmit}>
              <img src="/assets/search-white.svg" alt="Search" width={14} height={14} />{' '}
              Search
            </SearchBtn>
            <Tooltip title="Coming soon..." arrow>
              <AskAssistantBtn>
                <img src="/assets/chatbot.svg" alt="" width={18} height={18} />
                <div>Ask Enclave Assistant</div>
              </AskAssistantBtn>
            </Tooltip>
          </SearchSection>
          <TryAskingSection>
            <TryAskingLabel>Try Asking</TryAskingLabel>
            {tryAskingSuggestions.map((suggestion) => (
              <Tooltip key={suggestion} title="Coming soon..." arrow>
                <TryAskingChip>{suggestion}</TryAskingChip>
              </Tooltip>
            ))}
          </TryAskingSection>
        </HeroContent>
        <HeroImage src="/assets/knowledge-hub.svg" alt="Knowledge Hub" />
      </HeroSection>



      <ContentRow>
        <TopicsColumn>
          <SectionHeader>
            <SectionTitle>Explore TDSP Enclave</SectionTitle>
            {topics.length > 6 && (
              <ViewAllLink
                to="#"
                onClick={(event) => {
                  event.preventDefault();
                  setShowAllTopics((current) => !current);
                }}
              >
                {showAllTopics ? 'View less' : 'View all'} &gt;
              </ViewAllLink>
            )}
          </SectionHeader>
          <TopicCardsGrid>
            {loadingTopics
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TopicCard key={i}>
                    <TopicCardHeader>
                      <Skeleton variant="circular" width={36} height={36} />
                      <Skeleton variant="text" width="60%" height={24} />
                    </TopicCardHeader>
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="30%" height={20} sx={{ mt: 1 }} />
                  </TopicCard>
                ))
              : visibleTopics.map((topic) => {
              const topicDescription = truncateWords(topic.description);
              return (
              <TopicCard key={topic.id} onClick={() => navigate(`/explore-topics/${topic.id}`)}>
                <TopicCardHeader>
                  <TopicCardIconWrapper>
                    <img src={topic.icon} alt="" />
                  </TopicCardIconWrapper>
                  <ClampedTitle title={topic.title} as={TopicCardTitle} />
                </TopicCardHeader>
                {topicDescription.truncated ? (
                  <Tooltip title={topic.description} arrow>
                    <TopicCardDescription>{topicDescription.text}</TopicCardDescription>
                  </Tooltip>
                ) : (
                  <TopicCardDescription>{topicDescription.text}</TopicCardDescription>
                )}
                <TopicCardDocsCount>
                  <span>{topic.docsCount ?? 0} Docs</span>
                  <ActionArrow><img src="/assets/action-arrow.svg" alt="" width={8.13} height={13.97} /></ActionArrow>
                </TopicCardDocsCount>
              </TopicCard>
              );
            })}
          </TopicCardsGrid>
        </TopicsColumn>

        <RecentlyViewedSection>
          <SectionHeader>
            <SectionTitle>Recently Viewed</SectionTitle>
            {/* <ViewAllLink to="#">View all &gt;</ViewAllLink> */}
          </SectionHeader>
          <RecentlyViewedBox>
            {loadingRecent
              ? Array.from({ length: 5 }).map((_, i) => (
                  <RecentlyViewedItem key={i}>
                    <RecentlyViewedIconBadge>
                      <Skeleton variant="circular" width={32} height={32} />
                    </RecentlyViewedIconBadge>
                    <div style={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" height={20} />
                      <Skeleton variant="text" width="40%" height={16} />
                    </div>
                  </RecentlyViewedItem>
                ))
              : recentlyViewed.map((item) => (
                  <RecentlyViewedItem
                    key={item.id}
                    onClick={() => navigate(`/documentation/${item.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <RecentlyViewedIconBadge>
                      <RecentlyViewedIcon src="/assets/documentation-icon.svg" alt="" />
                    </RecentlyViewedIconBadge>
                    <div>
                      <RecentlyViewedItemTitle>{item.title}</RecentlyViewedItemTitle>
                      <RecentlyViewedItemTime>Viewed {formatRelativeTime(item.viewedAt)}</RecentlyViewedItemTime>
                    </div>
                  </RecentlyViewedItem>
                ))}
            {!loadingRecent && recentlyViewed.length === 0 && (
              <RecentlyViewedEmpty>
                <RecentlyViewedEmptyIcon>
                  <img src="/assets/documentation-icon.svg" alt="" />
                </RecentlyViewedEmptyIcon>
                <RecentlyViewedEmptyTitle>No documents viewed yet.</RecentlyViewedEmptyTitle>
                <RecentlyViewedEmptyText>
                  Start exploring to see your recently viewed documents here.
                </RecentlyViewedEmptyText>
              </RecentlyViewedEmpty>
            )}
          </RecentlyViewedBox>
        </RecentlyViewedSection>
      </ContentRow>
      
      <TopicCardsGridHowTo>
        <SectionHeaderHowTo>
        <SectionTitle>How to?</SectionTitle>
      </SectionHeaderHowTo>

        {loadingHowTo
          ? Array.from({ length: 4 }).map((_, i) => (
              <HowToCard key={i}>
                <HowToCardIcon>
                  <Skeleton variant="circular" width={32} height={32} />
                </HowToCardIcon>
                <HowToCardContent>
                  <Skeleton variant="text" width="50%" height={22} />
                  <Skeleton variant="text" width="80%" height={18} />
                </HowToCardContent>
              </HowToCard>
            ))
          : howToItems.map((item) => {
          const howToDescription = truncateWords(item.description);
          return (
          <HowToCard key={item.id} onClick={() => navigate(`/documentation/${item.pageId}`)}>
            <HowToCardIcon>
              <img src={item.icon} alt="" />
            </HowToCardIcon>
            <HowToCardContent>
              <ClampedTitle title={item.title} as={HowToCardTitle} />
              {howToDescription.truncated ? (
                <Tooltip title={item.description} arrow>
                  <HowToCardDescription>{howToDescription.text}</HowToCardDescription>
                </Tooltip>
              ) : (
                <HowToCardDescription>{howToDescription.text}</HowToCardDescription>
              )}
            </HowToCardContent>
          </HowToCard>
          );
        })}
      </TopicCardsGridHowTo>
    </PageWrapper>
  );
}

export default ExploreTopics;
